import React, { useState, useEffect, useRef } from 'react';
import { Users, Package, Award, Globe } from 'lucide-react';

const stats = [
  {
    icon: Users,
    value: 150000,
    suffix: '+',
    label: 'Happy Customers',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  {
    icon: Package,
    value: 5000,
    suffix: '+',
    label: 'Products Available',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
  },
  {
    icon: Award,
    value: 15,
    suffix: '+',
    label: 'Years Experience',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    icon: Globe,
    value: 50,
    suffix: '+',
    label: 'Countries Served',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
];

const StatsCounter = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState(stats.map(() => 0));
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const frameDuration = 1000 / 60;
    const totalFrames = Math.round(duration / frameDuration);

    let frame = 0;
    const counter = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);

      setCounts(
        stats.map((stat) => Math.round(stat.value * easeOutQuart))
      );

      if (frame === totalFrames) {
        clearInterval(counter);
      }
    }, frameDuration);

    return () => clearInterval(counter);
  }, [isVisible]);

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(num >= 10000 ? 0 : 1) + 'K';
    }
    return num.toLocaleString();
  };

  return (
    <section
      ref={sectionRef}
      className="py-16 bg-gradient-to-r from-teal-600 via-teal-700 to-cyan-700 relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl transform translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className="text-center group"
              >
                {/* Icon */}
                <div className={`w-16 h-16 ${stat.bgColor} rounded-2xl flex items-center justify-center mx-auto mb-4 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  <Icon className={`w-8 h-8 ${stat.color}`} />
                </div>
                
                {/* Counter */}
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {formatNumber(counts[index])}
                  <span className="text-teal-200">{stat.suffix}</span>
                </div>
                
                {/* Label */}
                <p className="text-teal-100 font-medium">{stat.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsCounter;
